import { FtueVideoChapter } from '../../../api/core'
import { formatSeconds } from '../../../features/ftue-videos/components/FtueVideoPlayer/formatSeconds'

/** A chapter as edited; the backend assigns `chapterId` when the video is saved. */
export type EditableFtueChapter = Omit<FtueVideoChapter, 'chapterId'> & { chapterId?: number }

export const CHAPTER_NUDGE_MS = 1000

export const formatChapterTime = (dataSeek: number) => formatSeconds(dataSeek / 1000)

/** Stable, so chapters at the same time keep their order. */
export const sortChapters = (chapters: EditableFtueChapter[]) => [...chapters].sort((a, b) => a.dataSeek - b.dataSeek)

/** Whole milliseconds between 0 and the video's length (when known); the backend truncates fractions. */
export const clampChapterSeek = (dataSeek: number, durationMs?: number) => {
  const max = durationMs && durationMs > 0 ? Math.round(durationMs) : Number.POSITIVE_INFINITY
  return Math.min(Math.max(Math.round(dataSeek), 0), max)
}

export const addChapterAt = (chapters: EditableFtueChapter[], seconds: number, durationMs?: number) =>
  sortChapters([...chapters, { titles: { en: '', ja: '', zh: '' }, tags: [], dataSeek: clampChapterSeek(seconds * 1000, durationMs) }])

export const nudgeChapter = (chapters: EditableFtueChapter[], index: number, deltaMs: number, durationMs?: number) =>
  sortChapters(chapters.map((chapter, i) => (i === index ? { ...chapter, dataSeek: clampChapterSeek(chapter.dataSeek + deltaMs, durationMs) } : chapter)))

export const removeChapterAt = (chapters: EditableFtueChapter[], index: number) => chapters.filter((_, i) => i !== index)

export const setChapterTitles = (chapters: EditableFtueChapter[], index: number, titles: EditableFtueChapter['titles']) =>
  chapters.map((chapter, i) => (i === index ? { ...chapter, titles } : chapter))

/** The stored chapter fields only, sorted, in whole milliseconds. */
export const toChapterBodies = (chapters: EditableFtueChapter[]): EditableFtueChapter[] =>
  sortChapters(chapters).map(({ chapterId, titles, tags, dataSeek }) => ({
    ...(chapterId !== undefined ? { chapterId } : {}),
    titles,
    tags,
    dataSeek: clampChapterSeek(dataSeek),
  }))

/** Compares what customers see; `chapterId` isn't compared because the backend renumbers chapters by position. */
export const chaptersMatch = (sent: EditableFtueChapter[], stored: EditableFtueChapter[]) =>
  sent.length === stored.length &&
  sent.every((chapter, i) => {
    const other = stored[i]
    return (
      chapter.dataSeek === other.dataSeek &&
      JSON.stringify(chapter.tags ?? []) === JSON.stringify(other.tags ?? []) &&
      translationsMatch(chapter.titles, other.titles)
    )
  })

/** Missing and empty languages are the same thing to the SaaS. */
export const translationsMatch = (a: { [lang: string]: string } = {}, b: { [lang: string]: string } = {}) =>
  Array.from(new Set([...Object.keys(a), ...Object.keys(b)])).every((lang) => (a[lang] ?? '') === (b[lang] ?? ''))
